import { nullable2undefinable } from 'core/utils/commonUtils';
import { http } from 'core/utils/httpClient';
import type { PaginatedList } from './dtoModels/paginatedList';
import type { Paginator } from './dtoModels/paginator';
import type { CreateOrUpdateProductRequest } from './dtoModels/product/createOrUpdateProductRequest';
import type { GetProductsRequest } from './dtoModels/product/getProductsRequest';
import type { ProductDetailResponse } from './dtoModels/product/productDetailResponse';
import type { ProductResponse } from './dtoModels/product/productResponse';
import type { Product } from 'models/product/product';
import type { ProductDetail } from 'models/product/productDetail';

const mapProduct = (source: ProductResponse): Product => ({
  ...source,
  groupId: nullable2undefinable(source.groupId),
});

const mapProducts = (source: ProductResponse[]): Product[] =>
  source.map((s) => mapProduct(s));

const mapProductDetail = (source: ProductDetailResponse): ProductDetail => ({
  ...source,
  groupId: nullable2undefinable(source.groupId),
});

export async function getProductsAsync(
  request: GetProductsRequest & Paginator
): Promise<PaginatedList<Product>> {
  const { data } = await http.get<PaginatedList<ProductResponse>>('products', {
    params: request,
  });

  return { ...data, items: mapProducts(data.items) };
}

export async function getProductDetailAsync(productId: number): Promise<ProductDetail> {
  const { data } = await http.get<ProductDetailResponse>(`products/${productId}`);
  return mapProductDetail(data);
}

export async function createProductAsync(
  request: CreateOrUpdateProductRequest
): Promise<Product> {
  const { data } = await http.post<ProductResponse>('products', request);
  return mapProduct(data);
}

export async function saveProductAsync(
  productId: number,
  request: CreateOrUpdateProductRequest
): Promise<Product> {
  const { data } = await http.put<ProductResponse>(`products/${productId}`, request);
  return mapProduct(data);
}

export async function deleteProductAsync(productIds: number[]): Promise<void> {
  const { data } = await http.delete(`products`, { params: { ids: productIds } });
  return data;
}

export async function moveProductsToGroupAsync(
  productIds: number[],
  groupId?: number
): Promise<void> {
  const { data } = await http.put(`products/move`, productIds, { params: { groupId } });
  return data;
}
